import type { KeyboardEventHandler } from "react";
import { useEffect, useRef } from "react";

import { useCalendarContext } from "../../../context";
import { KEYS } from "../../../constants";
import { parseDate, getOffset, isDateInInterval, isArrow, getDateParts } from "../../../helpers";
import { DateButton } from "../../../DateButton/DateButton";

type Props = {
  "data-date": string;
  firstWeekDay: number;
  lastWeekDay: number;
  currentWeekDay: number;
};

export function Day({
  firstWeekDay,
  lastWeekDay,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- разобраться, нужно ли
  currentWeekDay,
  ...props
}: Props) {
  const {
    visible,
    currentDate,
    focusedDate,
    selectedDate,
    selectionType,
    disabledDates,
    minDate,
    maxDate,
    onInnerDateChange,
    onSelectedDateChange,
  } = useCalendarContext();
  const ref = useRef<HTMLButtonElement>(null);

  const date = props["data-date"];
  const originalDate = new Date(date);
  const { day, month, year } = getDateParts(date);
  const isDisabled =
    (minDate && new Date(minDate).getTime() > originalDate.getTime()) ||
    (maxDate && new Date(maxDate).getTime() < originalDate.getTime()) ||
    disabledDates.includes(date);
  const isCurrentDate = date === currentDate;

  let selected = false;
  let tabIndex = -1;
  let isInInterval = false;
  if (selectedDate) {
    selected = selectedDate.some((item) => item === date);
    if (!selected && selectionType === "interval" && selectedDate.length === 2) {
      isInInterval = isDateInInterval(selectedDate[0], selectedDate[1], date);
    }
  }
  if (isCurrentDate) {
    tabIndex = 0;
  }

  const handleButtonKeydown: KeyboardEventHandler = (event) => {
    const { key, shiftKey } = event;
    const isHome = key === KEYS.HOME;
    const isEnd = key === KEYS.END;
    const isPageUp = key === KEYS.PAGE_UP;
    const isPageDown = key === KEYS.PAGE_DOWN;
    if (isArrow(key) || isHome || isEnd || isPageUp || isPageDown) {
      event.preventDefault();
      let neighbouringDay = day;
      let neighbouringMonth = month;
      let neighbouringYear = year;
      if (isArrow(key)) {
        neighbouringDay = day + getOffset("days", key);
      } else if (isHome) {
        neighbouringDay = firstWeekDay;
      } else if (isEnd) {
        neighbouringDay = lastWeekDay;
      } else if (isPageUp || isPageDown) {
        if (shiftKey) {
          neighbouringYear = isPageUp ? year - 1 : year + 1;
        } else {
          neighbouringMonth = isPageUp ? month - 1 : month + 1;
        }
      }
      const newDate = new Date(neighbouringYear, neighbouringMonth, neighbouringDay);
      const newCurrentDate = parseDate(newDate);
      onInnerDateChange({ date: newCurrentDate, updateFocus: true });
    }
  };

  const handleClick = () => {
    if (isDisabled) return;
    onInnerDateChange({ date, updateFocus: true });
    onSelectedDateChange(date);
  };

  useEffect(
    function updateDayFocus() {
      const isCurrentDayAlreadyFocused = ref.current?.isEqualNode(document.activeElement);
      if (!isCurrentDayAlreadyFocused && date === focusedDate && visible) {
        ref.current?.focus();
      }
    },
    [focusedDate, date, visible]
  );

  return (
    <DateButton
      ref={ref}
      tabIndex={tabIndex}
      selected={selected}
      isInInterval={isInInterval}
      disabled={isDisabled}
      onKeyDown={handleButtonKeydown}
      onClick={handleClick}
      {...props}
      styles={{
        fontFeatureSettings: "'tnum'",
      }}
    >
      {day}
    </DateButton>
  );
}
